import React from "react";
import {
  Building2,
  Mail,
  Phone,
  MapPin,
  Globe,
  CheckCircle,
  XCircle,
  BadgeIndianRupee,
  Landmark,
  Link2,
  Facebook,
  Twitter,
  Instagram,
  Youtube,
  X,
} from "lucide-react";

function CompanyDetails({ companyData }) {
  if (!companyData) return null;

  const formatName = (str) => str?.replace(/_/g, " ");

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 space-y-8">
      
      {/* ===== Header ===== */}
      <div className="flex items-center justify-between flex-wrap gap-6">
        <div className="flex items-center gap-4">
          {companyData.companyLogo ? (
            <img
              src={process.env.REACT_APP_IMAGE_URL + companyData.companyLogo}
              alt="Company Logo"
              className="w-24 h-24 bg-blue-900 text-gray-300 rounded-xl object-contain border border-gray-200"
            />
          ) : (
            <div className="w-24 h-24 flex items-center justify-center rounded-xl bg-gray-100 border border-gray-200">
              <Building2 className="w-10 h-10 text-gray-400" />
            </div>
          )}

          <div>
            <h2 className="text-2xl font-semibold text-gray-800">
              {formatName(companyData.companyName)}
            </h2>

            {companyData.companyCin && (
              <p className="text-sm text-gray-500 mt-1">
                CIN: {companyData.companyCin}
              </p>
            )}

            <div className="mt-2">
              {companyData.isActive ? (
                <span className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full bg-green-100 text-green-700">
                  <CheckCircle size={14} />
                  Active
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full bg-red-100 text-red-600">
                  <XCircle size={14} />
                  Inactive
                </span>
              )}
            </div>
          </div>
        </div>

        {/* QR */}
        {companyData.companyQr && (
          <img
            src={process.env.REACT_APP_IMAGE_URL + companyData.companyQr}
            alt="Company QR"
            className="w-24 h-24 object-contain border rounded-lg"
          />
        )}
      </div>

      {/* ===== Contact & Address ===== */}
      <div className="grid md:grid-cols-2 gap-6 text-sm">
        {companyData.companyEmail && (
          <div className="flex items-center gap-3 text-gray-600">
            <Mail size={16} />
            {companyData.companyEmail}
          </div>
        )}

        {companyData.companyContact && (
          <div className="flex items-center gap-3 text-gray-600">
            <Phone size={16} />
            {companyData.companyContact}
          </div>
        )}

        {companyData.companyWhatsappNo && (
          <div className="flex items-center gap-3 text-gray-600">
            <Phone size={16} />
            WhatsApp: {companyData.companyWhatsappNo}
          </div>
        )}

        {companyData.companyUrl && (
          <div className="flex items-center gap-3 text-gray-600">
            <Globe size={16} />
            <a
              href={companyData.companyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              {companyData.companyUrl}
            </a>
          </div>
        )}

        {companyData.corporateOfficeAddress && (
          <div className="flex items-start gap-3 text-gray-600">
            <MapPin size={16} className="mt-1" />
            {companyData.corporateOfficeAddress}
          </div>
        )}

        {companyData.headOfficeAddress && (
          <div className="flex items-start gap-3 text-gray-600">
            <Landmark size={16} className="mt-1" />
            {companyData.headOfficeAddress}
          </div>
        )}
      </div>

      {/* ===== Capital & Apps ===== */}
      <div className="grid md:grid-cols-2 gap-6 text-sm">
        {companyData.companyCapital && (
          <div className="flex items-center gap-3 text-gray-600">
            <BadgeIndianRupee size={16} />
            Capital: ₹{Number(companyData.companyCapital).toLocaleString()}
          </div>
        )}

        {companyData.supportUrl && (
          <div className="flex items-center gap-3 text-gray-600">
            <Link2 size={16} />
            <a
              href={companyData.supportUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              Support Portal
            </a>
          </div>
        )}

        {companyData.patientApp && (
          <div>
            <a
              href={companyData.patientApp}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 text-sm hover:underline"
            >
              Patient App
            </a>
          </div>
        )}

        {companyData.employeeApp && (
          <div>
            <a
              href={companyData.employeeApp}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 text-sm hover:underline"
            >
              Employee App
            </a>
          </div>
        )}
      </div>

    

{/* ===== Social Media ===== */}
<div className="flex items-center gap-4 pt-4 border-t border-gray-100">
  
  {companyData.facebookUrl && (
    <a
      href={companyData.facebookUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="p-3 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-600 hover:text-white transition"
    >
      <Facebook size={18} />
    </a>
  )}

  {companyData.twitterUrl && (
    <a
      href={companyData.twitterUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-500 hover:text-white transition"
    >
      <X size={28} />
    </a>
  )}

  {companyData.instagramUrl && (
    <a
      href={companyData.instagramUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="p-3 rounded-full bg-pink-100 text-pink-600 hover:bg-pink-600 hover:text-white transition"
    >
      <Instagram size={18} />
    </a>
  )}

  {companyData.youtubeUrl && (
    <a
      href={companyData.youtubeUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="p-3 rounded-full bg-red-100 text-red-600 hover:bg-red-600 hover:text-white transition"
    >
      <Youtube size={18} />
    </a>
  )}

</div>

      {/* ===== Metadata ===== */}
      <div className="text-xs text-gray-400 border-t pt-4">
        Created: {new Date(companyData.created_at).toLocaleString()} <br />
        Updated: {new Date(companyData.updated_at).toLocaleString()}
      </div>
    </div>
  );
}

export default CompanyDetails;